import { ActivityIndicator, Pressable, ScrollView } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { sizes } from "@src/constants/sizes";
// @ts-expect-error baseline 2026-02-16
import { ReportsSection, ReportsListItem, ReportsListItemText } from "@src/components/web";
import { isWeb } from "@src/utils/web-helpers";
import { useRoadSenseReportList } from "@src/features/roadsense/hooks/useRoadSenseReportList";
import type { ReportListItem } from "@visioni/a3s-reports/client-messages";

/**
 * RoadSense **lifecycle list** — the landing screen at `…/reports/roadsense`.
 *
 * RoadSense navigation is lifecycle-first: each report row is one hire
 * lifecycle (most tenants have exactly one; tenants with multiple hires have
 * several). Picking a lifecycle drills into its site list
 * (`…/report/<reportId>`), then a site renders its report. RoadSense-owned
 * screen, a sibling of (not a fork of) the WasteSense picker.
 */
export default function RoadSenseLifecycleListScreen() {
  const { tenantId } = useLocalSearchParams<{ tenantId: string }>();
  const { theme } = useUnistyles();

  if (!isWeb) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" padding="3xl" gap="md">
        <Text textStyle="sm/semibold" color="text" textAlign="center">
          RoadSense reports are only available on the web client.
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Open this page in your browser to view your reports.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <View gap="6xl">
        <View gap="sm" px="md">
          <Text textStyle="lg/semibold" fontSize={22} style={{ color: theme.colors.red[600] }}>
            RoadSense
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            A clear picture of vehicle flow and traffic insights.
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            Pick a report to see the sites it covers.
          </Text>
        </View>

        <ReportsSection title="Reports">
          <LifecycleListBody tenantId={tenantId} />
        </ReportsSection>
      </View>
    </ScrollView>
  );
}

function LifecycleListBody({ tenantId }: { tenantId: string }) {
  const { reports, isLoading, isError, isTenantPending, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useRoadSenseReportList();

  if ((isLoading || isTenantPending) && reports.length === 0) {
    return (
      <View padding="3xl" alignItems="center" gap="md">
        <ActivityIndicator />
        <Text textStyle="xs/regular" color="textMuted">
          Loading reports…
        </Text>
      </View>
    );
  }

  if (isError) {
    return (
      <View padding="3xl" alignItems="center" gap="sm">
        <Text textStyle="sm/semibold" color="text">
          Couldn&apos;t load reports
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Something went wrong loading your reports. Please refresh the page to try again.
        </Text>
      </View>
    );
  }

  if (reports.length === 0) {
    return (
      <View padding="3xl" alignItems="center" gap="sm">
        <Text textStyle="sm/semibold" color="text">
          No reports yet
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Reports appear here once the first RoadSense digest has been generated for your account.
        </Text>
      </View>
    );
  }

  return (
    <View gap="md">
      {reports.map((row) => {
        const title = formatPeriodTitle(row.periodStart, row.periodEnd);
        // Non-`ready` rows would 409 on click, so render them as a static,
        // non-interactive card with a status-aware sub-label instead of a link.
        if (row.status !== "ready") {
          return <NonReadyReportRow key={row.reportId} title={title} status={row.status} />;
        }
        return (
          <ReportsListItem
            key={row.reportId}
            title={title}
            href={`/tenants/${tenantId}/reports/roadsense/report/${encodeURIComponent(row.reportId)}`}
          >
            <ReportsListItemText>{describeRow(row)}</ReportsListItemText>
          </ReportsListItem>
        );
      })}
      {hasNextPage ?
        <Pressable
          onPress={isFetchingNextPage ? undefined : () => fetchNextPage()}
          accessibilityRole="button"
          accessibilityState={{ disabled: isFetchingNextPage }}
          style={styles.loadMore}
        >
          {isFetchingNextPage ?
            <View flexDirection="row" gap="sm" alignItems="center">
              <ActivityIndicator />
              <Text textStyle="xs/regular" color="textMuted">
                Loading older reports…
              </Text>
            </View>
          : <Text textStyle="xs/semibold" color="text">
              Load older reports
            </Text>
          }
        </Pressable>
      : null}
    </View>
  );
}

// `timeZone: "UTC"` pins the formatters to the same zone the period dates are
// stamped in. Without it `Intl.DateTimeFormat` formats UTC midnight in the
// viewer's local zone, so a viewer behind UTC sees a `2026-06-01` row labelled
// "Sun 31 May".
const WEEKDAY_FMT = new Intl.DateTimeFormat("en-AU", { weekday: "short", timeZone: "UTC" });
const DAY_MONTH_FMT = new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", timeZone: "UTC" });
const DAY_MONTH_YEAR_FMT = new Intl.DateTimeFormat("en-AU", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});
// `generatedAt` is a wall-clock instant — the viewer's local zone is the right
// one for this label, so it has no `timeZone` override.
const GENERATED_AT_FMT = new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", year: "numeric" });

/**
 * Formats a row's period bounds as e.g. "Mon 26 May – Sun 1 Jun 2026". The
 * year shows on the closing date only unless the period straddles a year.
 */
function formatPeriodTitle(periodStart: string, periodEnd: string): string {
  const start = parseIsoCalendarDate(periodStart);
  const end = parseIsoCalendarDate(periodEnd);
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const startLabel = sameYear ? DAY_MONTH_FMT.format(start) : DAY_MONTH_YEAR_FMT.format(start);
  return `${WEEKDAY_FMT.format(start)} ${startLabel} – ${WEEKDAY_FMT.format(end)} ${DAY_MONTH_YEAR_FMT.format(end)}`;
}

/** `YYYY-MM-DD` → `Date` at UTC midnight. */
function parseIsoCalendarDate(s: string): Date {
  const [y, m, d] = s.split("-").map((p) => Number(p));
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}

/** Sub-label for a *ready* row — generation date when present, else "Ready". */
function describeRow(row: ReportListItem): string {
  if (row.generatedAt) {
    return `Generated ${GENERATED_AT_FMT.format(new Date(row.generatedAt))}`;
  }
  return "Ready";
}

/**
 * Static (non-clickable) card for a report that isn't `ready` yet. Mirrors the
 * `ReportsListItem` card chrome but without a link/chevron, since clicking
 * through would 409. Copy is split per status so users can tell a report
 * that's still processing apart from one that failed.
 */
function NonReadyReportRow({ title, status }: { title: string; status: Exclude<ReportListItem["status"], "ready"> }) {
  const subLabel = status === "generating" ? "Being generated" : "Not currently available";
  return (
    <View style={styles.nonReadyCard}>
      <Text textStyle="sm/regular" color="textMuted">
        {title}
      </Text>
      <Text textStyle="xs/regular" color="textMuted">
        {subLabel}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 40,
    maxWidth: sizes.webContentMaxWidth,
    width: "100%",
  },
  loadMore: {
    paddingVertical: 12,
    alignItems: "center",
  },
  // Mirrors the `ReportsListItem` card chrome (surface/border/radius) so a
  // non-clickable row sits flush in the same list, minus the link + chevron.
  nonReadyCard: {
    paddingVertical: theme.spacing["3xl"],
    paddingHorizontal: theme.spacing.xl,
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.md,
    minHeight: 56,
    gap: theme.spacing.md,
    justifyContent: "center",
  },
}));
